/**
 * ChartOfAccountsTab — Ustawienia → Plan kont.
 *
 * Drzewo planu kont (`chart_of_accounts`) PER FIRMA — endpoint `GET /chart-of-accounts
 * ?format=tree` wymaga dokładnie jednej firmy w zakresie (400 `COMPANY_ID_REQUIRED_FOR_TREE`
 * inaczej, patrz nagłówek `apps/api/app/api/chart-of-accounts/route.ts`). Firma:
 *  - top-bar w trybie JEDNEJ firmy (`useActiveCompanyScope().mode === 'company'`) →
 *    używamy jej wprost, bez dodatkowego pickera.
 *  - top-bar w trybie "Wszystkie firmy" (`mode === 'all'`) → drzewo nie ma sensu cross-company,
 *    więc pokazujemy LOKALNY picker firmy (niezależny od top-bara, jak `AccountSearchPicker`
 *    dla dekretacji) — dopóki user nie wybierze, renderujemy tylko prompt zamiast 400 z backendu.
 *
 * CRUD: dodaj/edytuj/usuń (soft)/przywróz konto — `<ConfirmDialog>` dla destrukcyjnych
 * i nie-destrukcyjnych potwierdzeń (CLAUDE.md §4.12, zero window.confirm). Edycja/usuwanie
 * bramkowane permission `documents.chart_of_accounts.manage`; sam odczyt `.view`.
 *
 * Wiersz filtrów pod nagłówkiem (numer / nazwa / typ / strona / flagi) — filtrowanie
 * LOKALNE, bo drzewo przychodzi całe jednym zapytaniem. Kontrolki i ich miary pożyczone z
 * `ConfigurableTable` (`FILTER_CONTROL_SIZE_CLASS`), żeby ta tabela nie wyglądała inaczej
 * niż pozostałe listy. Filtr PRZYCINA drzewo zachowując przodków trafień (`pruneTree`) i
 * rozwija to, co zostało — inaczej dopasowana analityka znikałaby razem z niedopasowanym
 * syntetykiem albo chowała się pod zwiniętym rodzicem.
 *
 * Drzewo — rekurencyjne wiersze tabeli z wcięciem wg głębokości + rozwiń/zwiń per węzeł
 * (mirror wzorca `toggleExpand` z `AdminKartoteki.tsx`). Auto-rozwinięcie CAŁEGO drzewa
 * przy pierwszym załadowaniu danej firmy (nie przy każdym refetchu — inaczej mutacja na
 * jednym koncie cofałaby ręczne zwinięcie innej gałęzi przez usera).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
} from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { FILTER_CONTROL_SIZE_CLASS } from '@/components/configurable-table/filter-row/control-classes';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { AdoptChartTemplateModal } from '@/components/settings/AdoptChartTemplateModal';
import {
  ChartOfAccountFormModal,
  type ChartOfAccountFormValues,
  type ChartOfAccountParentOption,
  emptyChartOfAccountFormValues,
} from '@/components/settings/ChartOfAccountFormModal';
import {
  type EligibleSymfoniaIntegration,
  ImportChartFromSymfoniaModal,
} from '@/components/settings/ImportChartFromSymfoniaModal';
import { Spinner } from '@/components/Spinner';
import { CompanyPicker } from '@/components/ui/CompanyPicker';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useCompanies } from '@/lib/use-permissions';

// ─── Typy ───────────────────────────────────────────────────────────────────

type AccountType = 'bilansowe' | 'wynikowe' | 'pozabilansowe';
type NormalSide = 'debit' | 'credit' | null;

interface AccountRow {
  id: string;
  companyId: string;
  parentId: string | null;
  accountNumber: string;
  name: string;
  accountType: AccountType;
  isSettlement: boolean;
  normalSide: NormalSide;
  isPostable: boolean;
  isActive: boolean;
  isBlocked: boolean;
  source: 'template' | 'manual' | 'import';
  gcrecord: string | null;
  /**
   * `null` = zwykłe konto. Inaczej wiersz jest POZIOMEM SŁOWNIKOWYM analityki — w Symfonii
   * konto rozwija się na tym poziomie po elementach słownika/kartoteki, a nie po ręcznie
   * zakładanych kontach (migracja 0154). Takich elementów NIE listujemy w drzewie (plan kont
   * stałby się nieczytelny) — pokazujemy jeden węzeł i dajemy przejście do zawartości.
   */
  dictionarySource: 'dictionary' | 'contractors' | 'employees' | null;
  /** Kod lokalnego słownika z pozycjami z ERP — celownik dwukliku. `null` = brak powiązania. */
  dictionaryCode?: string | null;
}

interface AccountTreeNode extends AccountRow {
  children: AccountTreeNode[];
}

interface ChartOfAccountsTreeResponse {
  format: 'tree';
  companyId: string | null;
  accounts: AccountTreeNode[];
}

interface FiscalYearRow {
  fiscalYear: string;
  lastImportedAt: string;
}

interface FiscalYearsResponse {
  years: FiscalYearRow[];
}

const VIEW_PERM = 'documents.chart_of_accounts.view';
const MANAGE_PERM = 'documents.chart_of_accounts.manage';
const SYMFONIA_MANAGE_PERM = 'symfonia_integration.config.manage';

interface EligibleIntegrationRow {
  id: string;
  name: string;
  erpSystem: string;
  connectionMode: string;
  applicationGuidSet: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

interface FlatRow extends AccountTreeNode {
  depth: number;
}

function flattenTree(nodes: AccountTreeNode[], depth = 0): FlatRow[] {
  const out: FlatRow[] = [];
  for (const node of nodes) {
    out.push({ ...node, depth });
    out.push(...flattenTree(node.children, depth + 1));
  }
  return out;
}

/** Id konta + wszystkich jego potomków (BFS po `parentId` w spłaszczonej liście). */
function collectDescendantIds(all: FlatRow[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const row of all) {
      if (row.parentId && ids.has(row.parentId) && !ids.has(row.id)) {
        ids.add(row.id);
        changed = true;
      }
    }
  }
  return ids;
}

/** Kolejność opcji filtra kolumny TYP — ta sama domena co `accountType` w bazie. */
const ACCOUNT_TYPE_OPTIONS: AccountType[] = ['bilansowe', 'wynikowe', 'pozabilansowe'];

// ─── Wiersz filtrów ─────────────────────────────────────────────────────────

/** Stan wiersza filtrów; `''` = kolumna bez filtra. */
interface ChartFilters {
  number: string;
  name: string;
  accountType: string;
  /** `'debit'` | `'credit'` | `'none'` (konto bez ustawionej strony). */
  normalSide: string;
  /** Klucz flagi z kolumny FLAGI — patrz `matchesFlagFilter`. */
  flag: string;
}

const EMPTY_CHART_FILTERS: ChartFilters = {
  number: '',
  name: '',
  accountType: '',
  normalSide: '',
  flag: '',
};

/** Klucze filtra kolumny FLAGI — te same pojęcia, które kolumna pokazuje jako badge. */
const FLAG_FILTER_KEYS = [
  'settlement',
  'notPostable',
  'blocked',
  'inactive',
  'deleted',
  'dictionary',
] as const;

function matchesFlagFilter(row: AccountRow, flag: string): boolean {
  switch (flag) {
    case 'settlement':
      return row.isSettlement;
    case 'notPostable':
      return !row.isPostable;
    case 'blocked':
      return row.isBlocked;
    case 'inactive':
      return !row.isActive;
    case 'deleted':
      return row.gcrecord !== null;
    case 'dictionary':
      return row.dictionarySource !== null;
    default:
      return true;
  }
}

function includesCaseInsensitive(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.trim().toLowerCase());
}

function matchesFilters(row: AccountRow, filters: ChartFilters): boolean {
  if (filters.number && !includesCaseInsensitive(row.accountNumber, filters.number)) return false;
  if (filters.name && !includesCaseInsensitive(row.name, filters.name)) return false;
  if (filters.accountType && row.accountType !== filters.accountType) return false;
  if (filters.normalSide && (row.normalSide ?? 'none') !== filters.normalSide) return false;
  if (filters.flag && !matchesFlagFilter(row, filters.flag)) return false;
  return true;
}

/**
 * Przycina drzewo do wierszy przechodzących filtr, ZACHOWUJĄC przodków trafień — inaczej
 * dopasowana analityka zniknęłaby razem z niedopasowanym syntetykiem i nie dałoby się jej
 * zobaczyć w kontekście. Dzieci węzła, które same nie przeszły filtra, są odcinane: wynik
 * pokazuje dokładnie trafienia plus ścieżkę do nich, nie całe gałęzie.
 */
function pruneTree(nodes: AccountTreeNode[], filters: ChartFilters): AccountTreeNode[] {
  const out: AccountTreeNode[] = [];
  for (const node of nodes) {
    const children = pruneTree(node.children, filters);
    if (children.length > 0 || matchesFilters(node, filters)) {
      out.push({ ...node, children });
    }
  }
  return out;
}

function formToValues(row: AccountRow): ChartOfAccountFormValues {
  return {
    accountNumber: row.accountNumber,
    name: row.name,
    parentId: row.parentId ?? '',
    accountType: row.accountType,
    isSettlement: row.isSettlement,
    normalSide: row.normalSide ?? '',
    isPostable: row.isPostable,
    isActive: row.isActive,
    isBlocked: row.isBlocked,
  };
}

// ─── Główny komponent ────────────────────────────────────────────────────────

export function ChartOfAccountsTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isSuperUser = me?.user.isSuperUser === true;
  const canView = isSuperUser || (me?.permissions.includes(VIEW_PERM) ?? false);
  const canManage = isSuperUser || (me?.permissions.includes(MANAGE_PERM) ?? false);
  const canImportSymfonia =
    canManage && (isSuperUser || (me?.permissions.includes(SYMFONIA_MANAGE_PERM) ?? false));

  const companyScope = useActiveCompanyScope();
  const companiesQ = useCompanies();
  const companies = useMemo(() => companiesQ.data?.companies ?? [], [companiesQ.data]);

  // Tryb "Wszystkie firmy" w top-barze → picker lokalny (drzewo wymaga jednej firmy).
  const [manualCompanyId, setManualCompanyId] = useState<string | null>(null);
  const effectiveCompanyId =
    companyScope.mode === 'company' ? companyScope.companyId : manualCompanyId;

  const [showDeleted, setShowDeleted] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const autoExpandedForRef = useRef<string | null>(null);

  // Rok obrachunkowy (migracja 0141 — plan kont jest per firma + rok). `null` = jeszcze
  // nierozstrzygnięty (czekamy na `yearsQ` niżej, żeby wybrać domyślnie najnowszy).
  const [fiscalYear, setFiscalYear] = useState<string | null>(null);
  const [addingYear, setAddingYear] = useState(false);
  const [newYearDraft, setNewYearDraft] = useState('');

  const yearsQ = useQuery({
    queryKey: ['chart-of-accounts', 'years', effectiveCompanyId],
    queryFn: async () => {
      const res = await api.get<FiscalYearsResponse>(
        `/chart-of-accounts/years?companyId=${effectiveCompanyId}`,
      );
      return res.data.years;
    },
    enabled: (canView || canManage) && !!effectiveCompanyId,
    retry: false,
  });
  const availableYears = useMemo(() => yearsQ.data ?? [], [yearsQ.data]);

  // Zmiana firmy — rok innej firmy nie ma sensu, wracamy do "nierozstrzygnięty".
  useEffect(() => {
    setFiscalYear(null);
    setAddingYear(false);
  }, [effectiveCompanyId]);

  // Domyślny wybór PO załadowaniu lat: najnowszy zaimportowany (`years[0]` — API sortuje
  // po MAX(created_at) desc, patrz `chart-of-accounts/years/route.ts`). Firma bez ŻADNEGO
  // roku zostaje z pustym polem — świadomie NIE proponujemy roku kalendarzowego: rok
  // obrachunkowy bywa alfanumeryczny i nie musi odpowiadać kalendarzowi, a podstawiony
  // "2026" wyglądałby jak istniejący rok, którego nikt nie zakładał. Pierwszy rok powstaje
  // wyłącznie jawnie: przyciskiem "Nowy rok" albo importem z Symfonii (rok bierze się wtedy
  // z wybranego roku FK).
  useEffect(() => {
    if (fiscalYear === null && availableYears.length > 0) {
      setFiscalYear(availableYears[0]!.fiscalYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearsQ.data]);

  const [createParentId, setCreateParentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AccountRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AccountRow | null>(null);
  const [pendingRestore, setPendingRestore] = useState<AccountRow | null>(null);
  const [adoptOpen, setAdoptOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const queryKey = [
    'chart-of-accounts',
    'tree',
    effectiveCompanyId,
    fiscalYear,
    showDeleted,
  ] as const;

  const treeQ = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ format: 'tree' });
      if (effectiveCompanyId) params.set('companyId', effectiveCompanyId);
      if (fiscalYear) params.set('fiscalYear', fiscalYear);
      if (showDeleted) params.set('include_deleted', 'true');
      const res = await api.get<ChartOfAccountsTreeResponse>(
        `/chart-of-accounts?${params.toString()}`,
      );
      return res.data;
    },
    // Nigdy nie odpalamy zapytania bez rozstrzygniętej firmy — unikamy 400
    // COMPANY_ID_REQUIRED_FOR_TREE, front sam pilnuje warunku (prompt niżej). Rok
    // czekamy na `fiscalYear` rozstrzygnięty (patrz efekt wyżej) — inaczej pierwszy fetch
    // zleciałby bez filtra i pokazałby wszystkie lata naraz.
    enabled: (canView || canManage) && !!effectiveCompanyId && !!fiscalYear,
    retry: false,
  });

  const accounts = useMemo(() => treeQ.data?.accounts ?? [], [treeQ.data]);
  const flatAll = useMemo(() => flattenTree(accounts), [accounts]);

  // Wiersz filtrów pod nagłówkiem tabeli — filtrowanie LOKALNE (drzewo i tak przychodzi
  // całe jednym zapytaniem), więc bez dotykania API.
  const [filters, setFilters] = useState<ChartFilters>(EMPTY_CHART_FILTERS);
  const hasActiveFilters = useMemo(() => Object.values(filters).some((v) => v !== ''), [filters]);
  function setFilter<K extends keyof ChartFilters>(key: K, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }
  // Zmiana firmy albo roku pokazuje inny plan kont — filtr po numerze/nazwie z poprzedniego
  // przestaje mieć sens i wyglądałby jak pusty plan.
  useEffect(() => {
    setFilters(EMPTY_CHART_FILTERS);
  }, [effectiveCompanyId, fiscalYear]);

  const visibleAccounts = useMemo(
    () => (hasActiveFilters ? pruneTree(accounts, filters) : accounts),
    [accounts, filters, hasActiveFilters],
  );
  // Przy aktywnym filtrze rozwijamy wszystko, co zostało — inaczej trafienie schowane
  // pod zwiniętym rodzicem byłoby niewidoczne mimo że przeszło filtr.
  const effectiveExpandedIds = useMemo(
    () => (hasActiveFilters ? new Set(flattenTree(visibleAccounts).map((r) => r.id)) : expandedIds),
    [hasActiveFilters, visibleAccounts, expandedIds],
  );

  // Pre-check przed pokazaniem przycisku importu — tylko integracje FK w trybie
  // bezpośrednim z ustawionym Application GUID kwalifikują się do importu planu kont.
  const eligibleIntegrationsQ = useQuery({
    queryKey: ['erp-integrations', effectiveCompanyId, 'symfonia-fk-direct'],
    queryFn: async () => {
      const res = await api.get<{ integrations: EligibleIntegrationRow[] }>(
        `/erp-integrations?companyId=${effectiveCompanyId}&isActive=true`,
      );
      return res.data.integrations;
    },
    enabled: canImportSymfonia && !!effectiveCompanyId,
    retry: false,
  });

  const eligibleIntegrations: EligibleSymfoniaIntegration[] = useMemo(
    () =>
      (eligibleIntegrationsQ.data ?? [])
        .filter(
          (i) =>
            i.erpSystem === 'symfonia_fk' && i.connectionMode === 'direct' && i.applicationGuidSet,
        )
        .map((i) => ({ id: i.id, name: i.name })),
    [eligibleIntegrationsQ.data],
  );

  // Moduł ERP nieaktywny dla tej firmy → nie reklamujemy funkcji, której firma nie kupiła.
  const symfoniaModuleInactive =
    isAxiosError(eligibleIntegrationsQ.error) &&
    eligibleIntegrationsQ.error.response?.status === 402;

  // Auto-rozwinięcie całego drzewa PRZY PIERWSZYM załadowaniu danej firmy — nie przy
  // każdym refetchu (inaczej mutacja na jednym koncie cofałaby ręczne zwinięcie innej
  // gałęzi przez usera).
  useEffect(() => {
    if (treeQ.data && autoExpandedForRef.current !== effectiveCompanyId) {
      setExpandedIds(new Set(flatAll.map((r) => r.id)));
      autoExpandedForRef.current = effectiveCompanyId;
    }
  }, [treeQ.data, effectiveCompanyId, flatAll]);

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['chart-of-accounts', 'tree', effectiveCompanyId] });
    queryClient.invalidateQueries({ queryKey: ['chart-of-accounts', 'years', effectiveCompanyId] });
  }

  function errorCode(err: unknown): string | null {
    return isAxiosError(err)
      ? ((err.response?.data as { error?: string } | undefined)?.error ?? null)
      : null;
  }

  function mapErrorMessage(code: string | null): string {
    switch (code) {
      case 'ACCOUNT_NUMBER_TAKEN':
        return t('tenant.chartOfAccounts.toast.numberTaken');
      case 'PARENT_CYCLE':
        return t('tenant.chartOfAccounts.toast.parentCycle');
      case 'PARENT_NOT_IN_COMPANY':
        return t('tenant.chartOfAccounts.toast.parentNotInCompany');
      case 'ACCOUNT_HAS_CHILDREN':
        return t('tenant.chartOfAccounts.toast.hasChildren');
      case 'COMPANY_ID_REQUIRED_FOR_TREE':
        return t('tenant.chartOfAccounts.toast.companyRequired');
      case 'COMPANY_NOT_FOUND':
        return t('tenant.chartOfAccounts.toast.companyNotFound');
      case 'ACCOUNT_NOT_FOUND':
        return t('tenant.chartOfAccounts.toast.notFound');
      case 'NOT_DELETED':
        return t('tenant.chartOfAccounts.toast.notDeleted');
      default:
        return t('tenant.chartOfAccounts.toast.genericError');
    }
  }

  const createMutation = useMutation({
    mutationFn: async (values: ChartOfAccountFormValues) => {
      if (!effectiveCompanyId) throw new Error('NO_COMPANY');
      if (!fiscalYear) throw new Error('NO_FISCAL_YEAR');
      const body = {
        companyId: effectiveCompanyId,
        fiscalYear,
        accountNumber: values.accountNumber,
        name: values.name,
        parentId: values.parentId || null,
        accountType: values.accountType,
        isSettlement: values.isSettlement,
        normalSide: values.normalSide || null,
        isPostable: values.isPostable,
        isActive: values.isActive,
        isBlocked: values.isBlocked,
      };
      const res = await api.post<{ account: AccountRow }>('/chart-of-accounts', body);
      return res.data.account;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.chartOfAccounts.toast.created') });
      setCreateOpen(false);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      values,
      original,
    }: {
      id: string;
      values: ChartOfAccountFormValues;
      original: AccountRow;
    }) => {
      const patch: Record<string, unknown> = {};
      if (values.accountNumber !== original.accountNumber)
        patch.accountNumber = values.accountNumber;
      if (values.name !== original.name) patch.name = values.name;
      const parentId = values.parentId || null;
      if (parentId !== original.parentId) patch.parentId = parentId;
      if (values.accountType !== original.accountType) patch.accountType = values.accountType;
      if (values.isSettlement !== original.isSettlement) patch.isSettlement = values.isSettlement;
      const normalSide = values.normalSide || null;
      if (normalSide !== original.normalSide) patch.normalSide = normalSide;
      if (values.isPostable !== original.isPostable) patch.isPostable = values.isPostable;
      if (values.isActive !== original.isActive) patch.isActive = values.isActive;
      if (values.isBlocked !== original.isBlocked) patch.isBlocked = values.isBlocked;
      if (Object.keys(patch).length === 0) return original;
      const res = await api.patch<{ account: AccountRow }>(`/chart-of-accounts/${id}`, patch);
      return res.data.account;
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.chartOfAccounts.toast.updated') });
      setEditing(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/chart-of-accounts/${id}`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.chartOfAccounts.toast.deleted') });
      setPendingDelete(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
      setPendingDelete(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/chart-of-accounts/${id}/restore`);
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.chartOfAccounts.toast.restored') });
      setPendingRestore(null);
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapErrorMessage(errorCode(err)) });
      setPendingRestore(null);
    },
  });

  function openCreate(parentId: string | null) {
    setCreateParentId(parentId);
    setCreateOpen(true);
  }

  function parentOptionsFor(excludeId: string | null): ChartOfAccountParentOption[] {
    const excluded = excludeId ? collectDescendantIds(flatAll, excludeId) : new Set<string>();
    return flatAll
      .filter((row) => row.gcrecord === null && !excluded.has(row.id))
      .map((row) => ({
        id: row.id,
        accountNumber: row.accountNumber,
        name: row.name,
        depth: row.depth,
      }));
  }

  const isModuleInactive = isAxiosError(treeQ.error) && treeQ.error.response?.status === 404;

  /**
   * Przejście z węzła słownikowego do ZAWARTOŚCI słownika:
   *  - słownik własny ERP → zakładka Słowniki z deep-linkiem na ten słownik,
   *  - kartoteka kontrahentów / pracowników → ich lista (zawartością słownika są tam
   *    rekordy kartoteki, nie pozycje słownika).
   * Gdy węzeł nie ma powiązanego słownika (import nie dociągnął pozycji, np. FK było
   * nieosiągalne), mówimy to wprost zamiast prowadzić w puste miejsce.
   */
  function openDictionary(row: AccountRow) {
    if (row.dictionarySource === 'contractors') {
      navigate('/contractors');
      return;
    }
    if (row.dictionarySource === 'employees') {
      navigate('/employees');
      return;
    }
    if (row.dictionaryCode) {
      navigate(`/settings?tab=dictionaries&dict=${encodeURIComponent(row.dictionaryCode)}`);
      return;
    }
    toast.show({ variant: 'info', title: t('tenant.chartOfAccounts.dictionary.notSynced') });
  }

  // Opcje selektora roku: lata z historii (importowane/z kontami) + aktualnie wybrany rok,
  // gdyby to był świeżo zaproponowany "nowy, pusty rok" jeszcze bez żadnego konta.
  const yearOptions = useMemo(() => {
    const codes = availableYears.map((y) => y.fiscalYear);
    if (fiscalYear && !codes.includes(fiscalYear)) codes.unshift(fiscalYear);
    return codes.map((code) => ({ value: code, label: code }));
  }, [availableYears, fiscalYear]);

  return (
    <div data-testid="chart-of-accounts-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.chartOfAccounts.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('tenant.chartOfAccounts.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && effectiveCompanyId && flatAll.length > 0 && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setExpandedIds(new Set(flatAll.map((r) => r.id)))}
              >
                {t('tenant.chartOfAccounts.expandAll')}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setExpandedIds(new Set())}>
                {t('tenant.chartOfAccounts.collapseAll')}
              </Button>
            </>
          )}
          {canManage && effectiveCompanyId && (
            <Button
              variant="secondary"
              leftIcon={<Sparkles className="h-4 w-4" />}
              onClick={() => setAdoptOpen(true)}
              disabled={!fiscalYear}
              title={!fiscalYear ? t('tenant.chartOfAccounts.yearRequired') : undefined}
              data-testid="chart-of-accounts-adopt-template-btn"
            >
              {t('tenant.chartOfAccounts.adopt.action')}
            </Button>
          )}
          {canImportSymfonia && effectiveCompanyId && !symfoniaModuleInactive && (
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={() => setImportOpen(true)}
              disabled={eligibleIntegrations.length === 0}
              title={
                eligibleIntegrations.length === 0
                  ? t('tenant.chartOfAccounts.importSymfonia.noIntegration')
                  : undefined
              }
              data-testid="chart-of-accounts-import-symfonia-btn"
            >
              {t('tenant.chartOfAccounts.importSymfonia.action')}
            </Button>
          )}
          {canManage && effectiveCompanyId && (
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => openCreate(null)}
              disabled={!fiscalYear}
              title={!fiscalYear ? t('tenant.chartOfAccounts.yearRequired') : undefined}
              data-testid="chart-of-account-new-btn"
            >
              {t('tenant.chartOfAccounts.newAccount')}
            </Button>
          )}
        </div>
      </header>

      {/* Firma — top-bar w trybie jednej firmy steruje bez pickera; tryb "Wszystkie
          firmy" wymaga lokalnego wyboru (drzewo nie ma sensu cross-company). */}
      {companyScope.mode !== 'company' && (
        <div className="mb-4 max-w-xs">
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            {t('tenant.chartOfAccounts.companyPickerLabel')}
          </label>
          <CompanyPicker
            value={manualCompanyId}
            onChange={setManualCompanyId}
            companies={companies.map((c) => ({ id: c.id, legalName: c.legalName }))}
            required
            testId="chart-of-accounts-company-picker"
          />
        </div>
      )}

      {/* Rok obrachunkowy — plan kont jest per (firma, rok) od migracji 0141. Wybór
          decyduje, który rok drzewa/tworzenia kont/adopcji wzorca widzimy — analogicznie
          do firmy wyżej, żaden rok nie jest "domyślnym kontekstem" API. */}
      {effectiveCompanyId && (
        <div className="mb-4 flex items-end gap-2">
          {addingYear ? (
            <>
              <Input
                label={t('tenant.chartOfAccounts.yearPickerLabel')}
                value={newYearDraft}
                onChange={(e) => setNewYearDraft(e.target.value)}
                placeholder={String(new Date().getFullYear())}
                containerClassName="max-w-[10rem]"
                autoFocus
                data-testid="chart-of-accounts-new-year-input"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const trimmed = newYearDraft.trim();
                  if (trimmed) setFiscalYear(trimmed);
                  setAddingYear(false);
                }}
                data-testid="chart-of-accounts-new-year-confirm-btn"
              >
                {t('common.confirm')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAddingYear(false)}>
                {t('common.cancel')}
              </Button>
            </>
          ) : (
            <>
              <Select
                label={t('tenant.chartOfAccounts.yearPickerLabel')}
                value={fiscalYear ?? ''}
                onChange={(e) => setFiscalYear(e.target.value || null)}
                options={yearOptions}
                placeholder={t('tenant.chartOfAccounts.yearPickerEmpty')}
                containerClassName="max-w-[10rem]"
                data-testid="chart-of-accounts-year-select"
              />
              {canManage && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => {
                    setNewYearDraft('');
                    setAddingYear(true);
                  }}
                  data-testid="chart-of-accounts-add-year-btn"
                >
                  {t('tenant.chartOfAccounts.newYear')}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {canManage && effectiveCompanyId && (
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="chart-of-accounts-show-deleted"
            className={
              'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ' +
              (showDeleted
                ? 'border-accent-500 bg-accent-500/10 text-accent-500'
                : 'border-border-primary bg-bg-primary text-text-secondary hover:border-accent-500 hover:text-accent-500')
            }
          >
            {showDeleted ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {t('tenant.chartOfAccounts.showDeleted')}
          </button>
        </div>
      )}

      {!canView && !canManage ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.chartOfAccounts.noPermission')}</p>
        </Card>
      ) : !effectiveCompanyId ? (
        <Card padding="md" data-testid="chart-of-accounts-no-company">
          <p className="text-sm text-text-tertiary">
            {t('tenant.chartOfAccounts.selectCompanyPrompt')}
          </p>
        </Card>
      ) : yearsQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : !fiscalYear ? (
        // Firma bez ani jednego roku obrachunkowego. Bez tego stanu user zobaczyłby
        // "Brak kont" (drzewo jest wyłączone bez roku) i nie wiedziałby, że najpierw
        // trzeba założyć rok.
        <Card padding="md" data-testid="chart-of-accounts-no-year">
          <p className="text-sm text-text-tertiary">{t('tenant.chartOfAccounts.noYearPrompt')}</p>
        </Card>
      ) : treeQ.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spinner />
        </div>
      ) : isModuleInactive ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.chartOfAccounts.moduleInactive')}</p>
        </Card>
      ) : treeQ.isError ? (
        <Card padding="md">
          <p className="text-sm text-danger-600">{mapErrorMessage(errorCode(treeQ.error))}</p>
        </Card>
      ) : accounts.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.chartOfAccounts.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="chart-of-accounts-tree">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.chartOfAccounts.list.colNumber')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.chartOfAccounts.list.colName')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.chartOfAccounts.list.colType')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.chartOfAccounts.list.colNormalSide')}
                  </th>
                  <th className="px-3 py-2 text-left">
                    {t('tenant.chartOfAccounts.list.colFlags')}
                  </th>
                  <th className="px-3 py-2" />
                </tr>

                {/* Wiersz filtrów — ten sam układ i te same miary kontrolek co w
                    `ConfigurableTable` (FILTER_CONTROL_SIZE_CLASS), żeby tabela planu kont
                    nie wyglądała inaczej niż pozostałe listy w apce. */}
                <tr className="border-b border-border-primary bg-bg-primary normal-case tracking-normal">
                  <th className="px-2 py-2 align-top">
                    <Input
                      type="text"
                      value={filters.number}
                      onChange={(e) => setFilter('number', e.target.value)}
                      aria-label={t('tenant.chartOfAccounts.list.colNumber')}
                      prefixIcon={<Search className="h-3.5 w-3.5" />}
                      className={FILTER_CONTROL_SIZE_CLASS}
                      data-testid="chart-of-accounts-filter-number"
                    />
                  </th>
                  <th className="px-2 py-2 align-top">
                    <Input
                      type="text"
                      value={filters.name}
                      onChange={(e) => setFilter('name', e.target.value)}
                      aria-label={t('tenant.chartOfAccounts.list.colName')}
                      prefixIcon={<Search className="h-3.5 w-3.5" />}
                      className={FILTER_CONTROL_SIZE_CLASS}
                      data-testid="chart-of-accounts-filter-name"
                    />
                  </th>
                  <th className="px-2 py-2 align-top">
                    <Dropdown
                      value={filters.accountType}
                      onChange={(val) => setFilter('accountType', val)}
                      options={[
                        { value: '', label: t('ui.configurableTable.selectFilter.all') },
                        ...ACCOUNT_TYPE_OPTIONS.map((type) => ({
                          value: type,
                          label: t(`tenant.chartOfAccounts.accountTypes.${type}`),
                        })),
                      ]}
                      aria-label={t('tenant.chartOfAccounts.list.colType')}
                      className={FILTER_CONTROL_SIZE_CLASS}
                      data-testid="chart-of-accounts-filter-type"
                    />
                  </th>
                  <th className="px-2 py-2 align-top">
                    <Dropdown
                      value={filters.normalSide}
                      onChange={(val) => setFilter('normalSide', val)}
                      options={[
                        { value: '', label: t('ui.configurableTable.selectFilter.all') },
                        ...(['debit', 'credit', 'none'] as const).map((side) => ({
                          value: side,
                          label: t(`tenant.chartOfAccounts.normalSides.${side}`),
                        })),
                      ]}
                      aria-label={t('tenant.chartOfAccounts.list.colNormalSide')}
                      className={FILTER_CONTROL_SIZE_CLASS}
                      data-testid="chart-of-accounts-filter-normal-side"
                    />
                  </th>
                  <th className="px-2 py-2 align-top">
                    <Dropdown
                      value={filters.flag}
                      onChange={(val) => setFilter('flag', val)}
                      options={[
                        { value: '', label: t('ui.configurableTable.selectFilter.all') },
                        ...FLAG_FILTER_KEYS.map((flag) => ({
                          value: flag,
                          label:
                            flag === 'dictionary'
                              ? t('tenant.chartOfAccounts.filters.flagDictionary')
                              : t(`tenant.chartOfAccounts.badges.${flag}`),
                        })),
                      ]}
                      aria-label={t('tenant.chartOfAccounts.list.colFlags')}
                      className={FILTER_CONTROL_SIZE_CLASS}
                      data-testid="chart-of-accounts-filter-flag"
                    />
                  </th>
                  <th className="px-2 py-2 align-top text-right">
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFilters(EMPTY_CHART_FILTERS)}
                        data-testid="chart-of-accounts-filter-clear"
                      >
                        {t('tenant.chartOfAccounts.filters.clear')}
                      </Button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {visibleAccounts.length === 0 && (
                  <tr data-testid="chart-of-accounts-filter-no-results">
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-text-tertiary">
                      {t('tenant.chartOfAccounts.filters.noResults')}
                    </td>
                  </tr>
                )}
                {visibleAccounts.map((node) => (
                  <AccountTreeRow
                    key={node.id}
                    node={node}
                    depth={0}
                    expandedIds={effectiveExpandedIds}
                    onToggleExpand={toggleExpand}
                    canManage={canManage}
                    onAddChild={openCreate}
                    onEdit={setEditing}
                    onDelete={setPendingDelete}
                    onRestore={setPendingRestore}
                    onOpenDictionary={openDictionary}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {effectiveCompanyId && fiscalYear && (
        <AdoptChartTemplateModal
          open={adoptOpen}
          companyId={effectiveCompanyId}
          fiscalYear={fiscalYear}
          onClose={() => setAdoptOpen(false)}
          onAdopted={invalidate}
        />
      )}

      {effectiveCompanyId && (
        <ImportChartFromSymfoniaModal
          open={importOpen}
          companyId={effectiveCompanyId}
          integrations={eligibleIntegrations}
          onClose={() => setImportOpen(false)}
          onImported={(importedYear) => {
            invalidate();
            setFiscalYear(importedYear);
          }}
        />
      )}

      <ChartOfAccountFormModal
        open={createOpen}
        mode="create"
        initialValues={emptyChartOfAccountFormValues(createParentId)}
        saving={createMutation.isPending}
        parentOptions={parentOptionsFor(null)}
        onSubmit={(values) => createMutation.mutate(values)}
        onClose={() => setCreateOpen(false)}
      />

      <ChartOfAccountFormModal
        open={editing !== null}
        mode="edit"
        initialValues={editing ? formToValues(editing) : emptyChartOfAccountFormValues()}
        saving={updateMutation.isPending}
        parentOptions={parentOptionsFor(editing?.id ?? null)}
        onSubmit={(values) => {
          if (editing) updateMutation.mutate({ id: editing.id, values, original: editing });
        }}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('tenant.chartOfAccounts.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.chartOfAccounts.confirmDelete.message', {
                number: pendingDelete.accountNumber,
                name: pendingDelete.name,
              })
            : ''
        }
        confirming={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingRestore !== null}
        variant="primary"
        title={t('tenant.chartOfAccounts.confirmRestore.title')}
        message={
          pendingRestore
            ? t('tenant.chartOfAccounts.confirmRestore.message', {
                number: pendingRestore.accountNumber,
                name: pendingRestore.name,
              })
            : ''
        }
        confirmLabel={t('common.restore')}
        confirming={restoreMutation.isPending}
        onConfirm={() => pendingRestore && restoreMutation.mutate(pendingRestore.id)}
        onClose={() => setPendingRestore(null)}
      />
    </div>
  );
}

// ─── Wiersz drzewa (rekurencyjny) ───────────────────────────────────────────

interface AccountTreeRowProps {
  node: AccountTreeNode;
  depth: number;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  canManage: boolean;
  onAddChild: (parentId: string) => void;
  onEdit: (row: AccountRow) => void;
  onDelete: (row: AccountRow) => void;
  onRestore: (row: AccountRow) => void;
  onOpenDictionary: (row: AccountRow) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function AccountTreeRow({
  node,
  depth,
  expandedIds,
  onToggleExpand,
  canManage,
  onAddChild,
  onEdit,
  onDelete,
  onRestore,
  onOpenDictionary,
  t,
}: AccountTreeRowProps) {
  const isDeleted = node.gcrecord !== null;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  // Dwuklik na węźle słownikowym → zawartość słownika. Zwykłe konta nie reagują —
  // nie ma tam nic do otwarcia.
  const isDictionary = node.dictionarySource !== null;

  return (
    <>
      <tr
        data-testid={`chart-of-account-row-${node.id}`}
        onDoubleClick={isDictionary ? () => onOpenDictionary(node) : undefined}
        title={isDictionary ? t('tenant.chartOfAccounts.dictionary.openHint') : undefined}
        className={
          (isDeleted || !node.isActive ? 'opacity-50 ' : '') +
          (isDictionary ? 'cursor-pointer bg-bg-secondary/40' : '')
        }
      >
        <td className="py-2 pr-2" style={{ paddingLeft: `${depth * 20 + 12}px` }}>
          <div className="flex items-center gap-1.5">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onToggleExpand(node.id)}
                aria-label={
                  isExpanded
                    ? t('tenant.chartOfAccounts.list.collapseNode')
                    : t('tenant.chartOfAccounts.list.expandNode')
                }
                data-testid={`chart-of-account-toggle-${node.id}`}
                className="rounded p-0.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="inline-block h-3.5 w-3.5" />
            )}
            {isDictionary && <BookOpen className="h-3.5 w-3.5 shrink-0 text-accent-500" />}
            <span className="font-mono text-xs text-text-secondary">{node.accountNumber}</span>
          </div>
        </td>
        <td className="px-3 py-2 font-medium text-text-primary">
          {node.name}
          {isDictionary && (
            <span className="ml-2 align-middle">
              <Badge color="accent">
                {t(`tenant.chartOfAccounts.dictionary.source.${node.dictionarySource}`)}
              </Badge>
            </span>
          )}
        </td>
        <td className="px-3 py-2">
          <Badge color="accent">
            {t(`tenant.chartOfAccounts.accountTypes.${node.accountType}`)}
          </Badge>
        </td>
        <td className="px-3 py-2 text-text-secondary">
          {node.normalSide ? t(`tenant.chartOfAccounts.normalSides.${node.normalSide}`) : '—'}
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-1">
            {node.isSettlement && (
              <Badge color="blue">{t('tenant.chartOfAccounts.badges.settlement')}</Badge>
            )}
            {!node.isPostable && (
              <Badge color="gray">{t('tenant.chartOfAccounts.badges.notPostable')}</Badge>
            )}
            {node.isBlocked && (
              <Badge color="red">{t('tenant.chartOfAccounts.badges.blocked')}</Badge>
            )}
            {!node.isActive && (
              <Badge color="gray">{t('tenant.chartOfAccounts.badges.inactive')}</Badge>
            )}
            {isDeleted && <Badge color="red">{t('tenant.chartOfAccounts.badges.deleted')}</Badge>}
          </div>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1">
            {/* Jawny przycisk obok dwukliku — sam dwuklik jest niewidoczny dla usera. */}
            {isDictionary && !isDeleted && (
              <IconButton
                size="sm"
                onClick={() => onOpenDictionary(node)}
                aria-label={t('tenant.chartOfAccounts.dictionary.open')}
                title={t('tenant.chartOfAccounts.dictionary.open')}
                data-testid={`chart-of-account-open-dictionary-${node.id}`}
              >
                <ExternalLink />
              </IconButton>
            )}
            {canManage && (
              <>
                {isDeleted ? (
                  <button
                    type="button"
                    onClick={() => onRestore(node)}
                    aria-label={t('common.restore')}
                    data-testid={`chart-of-account-restore-${node.id}`}
                    className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => onAddChild(node.id)}
                      aria-label={t('tenant.chartOfAccounts.list.addChild')}
                      data-testid={`chart-of-account-add-child-${node.id}`}
                      className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(node)}
                      aria-label={t('common.edit')}
                      data-testid={`chart-of-account-edit-${node.id}`}
                      className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(node)}
                      aria-label={t('common.delete')}
                      data-testid={`chart-of-account-delete-${node.id}`}
                      className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-danger-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
      {isExpanded &&
        node.children.map((child) => (
          <AccountTreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            onToggleExpand={onToggleExpand}
            canManage={canManage}
            onAddChild={onAddChild}
            onEdit={onEdit}
            onDelete={onDelete}
            onRestore={onRestore}
            onOpenDictionary={onOpenDictionary}
            t={t}
          />
        ))}
    </>
  );
}
